import { ROLE_PRECEDENCE } from "@/lib/utils/system-roles";

/**
 * Formats an RBAC / legacy role string into a human-facing label.
 * `lead_mentor` → `Lead Mentor`, `supervisor` → `Supervisor`, etc.
 *
 * Pass `{ supervisor: true }` to override and always return "Supervisor"
 * (used where supervisor capability needs to be surfaced regardless of the
 * user's primary role string).
 */
export function formatRoleLabel(
  role: string | null | undefined,
  options?: { supervisor?: boolean },
): string | null {
  if (options?.supervisor) return "Supervisor";
  if (!role) return null;

  return role
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function recordRoleLabels(
  roleNames: readonly string[] | undefined,
): string[] {
  const present = new Set(roleNames ?? []);
  const known = new Set<string>(ROLE_PRECEDENCE);
  const labels: string[] = [];

  for (const role of ROLE_PRECEDENCE) {
    if (!present.has(role)) continue;
    const label = formatRoleLabel(role);
    if (label) labels.push(label);
  }

  for (const role of roleNames ?? []) {
    if (known.has(role)) continue;
    const label = formatRoleLabel(role);
    if (label && !labels.includes(label)) labels.push(label);
  }

  return labels;
}
